import { FC, useEffect, useState } from 'react';
import { CustomInput, If } from 'components';
import { AttachFileIcon, SendLineIcon } from 'assets/icons';
import { JobTimelineEvent } from '../../jobs.model';
import JobTimeline from '../../components/JobTimeline';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../../events';
import JobDetailHistoryFilter from './JobDetailHistoryFilter';

interface HistoryTabProps {
  jobTimeline: JobTimelineEvent[];
  onEditNote: (event: JobTimelineEvent) => void;
  onAddNote: () => void;
  onDeleteNote: (event: JobTimelineEvent) => void;
  showPreview: (index: number, event: JobTimelineEvent) => void;
  onCreateNote: (note: string, clearInput: () => void) => void;
  showFilterOptions?: boolean;
}

const HistoryTab: FC<HistoryTabProps> = ({
  jobTimeline,
  onEditNote,
  onAddNote,
  onDeleteNote,
  showPreview,
  onCreateNote,
  showFilterOptions = true
}) => {
  const [currentNotesActionsPopoverId, setCurrentNotesActionsPopoverId] = useState(null);
  const [filteredTimeline, setFilteredTimeline] = useState<JobTimelineEvent[]>(jobTimeline || []);
  const [notesInput, setNotesInput] = useState('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedBaseFilter, setSelectedBaseFilter] = useState<string[]>([]);

  const getFilteredTimeline = () => {
    let result = jobTimeline || [];
    if (selectedActions.length > 0 || selectedBaseFilter.length > 0) {
      result = jobTimeline.filter(
        event => selectedBaseFilter.includes(event.type) || selectedActions.includes(event.subType)
      );
    }
    if (selectedBaseFilter?.length === 1 && selectedBaseFilter[0] === 'ATTACHMENTS') {
      const attachementResult = jobTimeline.filter(event => Boolean(event.attachments) && event.attachments.length > 0);
      result = [...result, ...attachementResult];
    }
    return result;
  };

  useEffect(() => {
    setFilteredTimeline(getFilteredTimeline());
  }, [jobTimeline, selectedActions, selectedBaseFilter]);

  const handleAnalyticEventForNotes = () => {
    logAnalyticEvent(JobsCleverTapEvents.webEnterNotesTextBoxClicked);
  };

  const onSendNoteClick = () => {
    onCreateNote(notesInput, () => setNotesInput(''));
  };

  const handleActionsChange = (actions: string[]) => {
    setSelectedActions(actions);
  };

  const handleBaseFilterChange = (baseFilter: string[]) => {
    setSelectedBaseFilter(baseFilter);
  };

  // eslint-disable-next-line react/no-multi-comp
  const NotesInputSuffix = () => (
    <div className="flex items-center gap-x-4">
      <AttachFileIcon
        id="job-history-notes-input-attach-file-icon"
        className="shrink-0 cursor-pointer"
        onClick={onAddNote}
      />
      <SendLineIcon
        id="job-history-notes-input-send-icon"
        className="shrink-0 cursor-pointer"
        width={18}
        height={18}
        onClick={onSendNoteClick}
      />
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col">
        <div className="px-6 pt-6 text-sbase font-bold text-primaryText">Note</div>
        <div className="mb-0.5 w-full px-6 pb-1 pt-2">
          <CustomInput
            id="job-history-notes-input"
            value={notesInput}
            onChange={setNotesInput}
            hasSuffix={true}
            showSuffixAlways={true}
            suffix={<NotesInputSuffix />}
            className="h-12 rounded-md border border-borderGray bg-transparent pr-[72px]
                    placeholder:font-semibold focus:outline-none"
            suffixStyle="right-4 top-4"
            placeholder="Enter note"
            onClick={handleAnalyticEventForNotes}
          />
        </div>
        <If condition={showFilterOptions}>
          <div className="flex gap-x-2.5 border-b border-secondaryBtn px-6 py-4">
            <JobDetailHistoryFilter
              selectedActions={selectedActions}
              onSelectActions={handleActionsChange}
              selectedBaseFilter={selectedBaseFilter}
              onSelectBaseFilter={handleBaseFilterChange}
            />
          </div>
        </If>
      </div>
      <div className="relative flex h-full flex-col px-1">
        {filteredTimeline.length > 0 ? (
          <JobTimeline
            jobTimeline={filteredTimeline}
            onEditNoteClick={onEditNote}
            onDeleteNoteClick={onDeleteNote}
            hasImageViewer={true}
            showPreview={showPreview}
            timelineId="jobs-history-timeline"
            currentActionsPopoverId={currentNotesActionsPopoverId}
            setCurrentActionsPopoverId={setCurrentNotesActionsPopoverId}
            timelineClassName="overflow-y-visible"
          />
        ) : (
          <div className="flex h-32 items-center px-5 text-accentText">No events to display</div>
        )}
      </div>
    </div>
  );
};

export default HistoryTab;
